import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Card, CardHeader, CardContent, Label, Tooltip, Button, Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui';
import { InfoIcon, ChevronUp, ChevronDown, Loader2, FileDown } from 'lucide-react';
import { exportToExcel, canExport } from '../utilities/excelExportUtils';
import apiService from '../services/apiService'; // Corrected import
import RMProFormaStatements from '../components/charts/RMProForma_FinancialStatements';
import RMCumulativePayback from '../components/charts/RMProForma_Payback';
import MonthlyAnnualIncomeStatement from '../components/charts/MonthlyAnnualIncomeStatement';
import MonthlyAnnualBalanceSheet from '../components/charts/MonthlyAnnualBalanceSheet';
import RMProFormaYieldCurve from '../components/charts/RMProForma_YieldCurve';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { validateDateInputs } from '../utilities/dateUtils';
import { getDefaultsForRMType, PRICING_DEFAULTS } from '../utilities/rmTypeDefaults';
import { consolidateResults } from '../utilities/consolidationUtils';
import ErrorBoundary from '../components/ErrorBoundary';
import { format, parseISO } from 'date-fns';

// SOFR-adjusted yield curves (Treasury - spread adjustment at short end)
const DEFAULT_YIELD_CURVES = {
  "2023": { "0.25": 0.0441, "0.5": 0.0457, "1": 0.0476, "2": 0.0439, "3": 0.0421, "5": 0.0391, "10": 0.0375, "30": 0.0391 },
  "2024": { "0.25": 0.0413, "0.5": 0.0429, "1": 0.0448, "2": 0.0411, "3": 0.0393, "5": 0.0363, "10": 0.0347, "30": 0.0363 },
  "2025": { "0.25": 0.0385, "0.5": 0.0401, "1": 0.0420, "2": 0.0383, "3": 0.0365, "5": 0.0335, "10": 0.0319, "30": 0.0335 }
};

const ensureYieldCurveCoverage = (curves) => {
  const merged = { ...DEFAULT_YIELD_CURVES, ...(curves || {}) };
  const years = Object.keys(merged)
    .map(y => Number(y))
    .filter(y => Number.isFinite(y))
    .sort((a, b) => a - b);

  if (years.length === 0) return DEFAULT_YIELD_CURVES;

  const latest = years[years.length - 1];
  const requiredYears = [latest - 1, latest - 2];

  requiredYears.forEach(year => {
    const key = String(year);
    if (!merged[key] && DEFAULT_YIELD_CURVES[key]) {
      merged[key] = DEFAULT_YIELD_CURVES[key];
    }
  });

  return merged;
};

const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Generate unique IDs for entries
const generateId = () => `entry_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Default form data for new RM entries
const getDefaultFormData = () => ({
  ...PRICING_DEFAULTS,
  rmType: '',
  rmHireDate: getTodayDate(),
  firstProductionDate: getTodayDate(),
  annualProduction: [
    { loans: 2000000, deposits: 500000 },  // Year 1
    { loans: 3000000, deposits: 750000 },  // Year 2
    { loans: 4000000, deposits: 1000000 }, // Year 3
    { loans: 5000000, deposits: 1250000 }, // Year 4
    { loans: 6000000, deposits: 1500000 }  // Year 5
  ],
  loanVsLinePercentage: 0.5,
  goingOnYields: [
    { loans: 0.0250, lines: 0.0250, deposits: 0.0050 }, // Year 1
    { loans: 0.0275, lines: 0.0275, deposits: 0.0075 }, // Year 2
    { loans: 0.0300, lines: 0.0300, deposits: 0.0100 }, // Year 3
    { loans: 0.0325, lines: 0.0325, deposits: 0.0125 }, // Year 4
    { loans: 0.0350, lines: 0.0350, deposits: 0.0150 }  // Year 5
  ],
  lineUtilizationPercentage: 0.5,
  originationFeePercentage: 0.0025,
  unusedCommitmentFeePercentage: 0.0025,
  salary: 75000,
  annualMeritIncrease: 0.03,
  incentiveCompensationPercentage: 0.20,
  discretionaryExpenses: 6000,
  deferredCostsPerLoan: 100,
  avgLoanExposureAtOrigination: 1000000,
  averageLifeLoans: 5,
  averageLifeLines: 3,
  prepayPercentageOfBalance: 0.15,
  // New toggle: if true, treat averageLifeLoans as WAL that already embeds prepayments
  // and ignore explicit prepayPercentageOfBalance on loans to avoid double counting
  averageLifeIncludesPrepay: false,
  provisionPercentage: 0.01,
});

// Default form data for support staff
const getDefaultSupportData = () => ({
  ...getDefaultFormData(),
  // Override with support-specific defaults
  rmType: 'support',
  annualProduction: [
    { loans: 0, deposits: 0 },
    { loans: 0, deposits: 0 },
    { loans: 0, deposits: 0 },
    { loans: 0, deposits: 0 },
    { loans: 0, deposits: 0 }
  ],
  goingOnYields: [
    { loans: 0, lines: 0, deposits: 0 },
    { loans: 0, lines: 0, deposits: 0 },
    { loans: 0, lines: 0, deposits: 0 },
    { loans: 0, lines: 0, deposits: 0 },
    { loans: 0, lines: 0, deposits: 0 }
  ],
  loanVsLinePercentage: 0,
  lineUtilizationPercentage: 0,
  originationFeePercentage: 0,
  unusedCommitmentFeePercentage: 0,
  salary: 50000,
  annualMeritIncrease: 0.03,
  incentiveCompensationPercentage: 0,
  discretionaryExpenses: 2000,
  deferredCostsPerLoan: 0,
  avgLoanExposureAtOrigination: 1,  // Avoid division by zero
  averageLifeLoans: 1,
  averageLifeLines: 1,
  prepayPercentageOfBalance: 0,
  averageLifeIncludesPrepay: false,
  provisionPercentage: 0,
});


const formatNumber = (value, options) => {
  const { isPercentage, decimals = 2, useCommas = true } = options;
  let formattedValue = parseFloat(value);

  if (isNaN(formattedValue)) return '';

  if (isPercentage) {
    formattedValue *= 100;
  }

  if (useCommas) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(formattedValue) + (isPercentage ? '%' : '');
  } else {
    return formattedValue.toFixed(decimals) + (isPercentage ? '%' : '');
  }
};

const parseNumber = (value, isPercentage) => {
  if (typeof value === 'number') return value;
  const cleaned = value.replace(/[^\d.-]/g, '');
  let parsed = parseFloat(cleaned);
  if (isPercentage) {
    parsed /= 100;
  }
  // Return NaN for invalid/incomplete input so callers can decide when to commit
  return Number.isNaN(parsed) ? NaN : parsed;
};

const CustomInput = ({ value, onChange, options = {} }) => {
  const {
    isPercentage,
    step,
    commitOnChange = true,
    disabled = false,
    decimals = 2,
    useCommas = true,
  } = options;
  const formatOpts = { isPercentage, decimals, useCommas };
  const [localValue, setLocalValue] = useState(formatNumber(value, formatOpts));
  const [flashDirection, setFlashDirection] = useState(null);

  useEffect(() => {
    setLocalValue(formatNumber(value, formatOpts));
    // Limit deps to stable fields to avoid cursor jumps from object identity changes
  }, [value, isPercentage, decimals, useCommas]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    const parsed = parseNumber(newValue, isPercentage);
    if (commitOnChange && !Number.isNaN(parsed)) {
      onChange(parsed);
    }
  };

  const handleBlur = () => {
    const parsed = parseNumber(localValue, isPercentage);
    if (!Number.isNaN(parsed)) {
      onChange(parsed);
      setLocalValue(formatNumber(parsed, formatOpts));
    } else {
      // Revert to last committed value
      setLocalValue(formatNumber(value, formatOpts));
    }
  };

  const handleIncrement = (increment) => {
    const base = parseNumber(localValue, isPercentage);
    const effectiveBase = Number.isNaN(base) ? (typeof value === 'number' ? value : parseNumber(String(value), isPercentage) || 0) : base;
    const newValue = effectiveBase + increment;
    setLocalValue(formatNumber(newValue, formatOpts));
    onChange(newValue);

    // Trigger flash effect based on direction
    setFlashDirection(increment > 0 ? 'up' : 'down');
    setTimeout(() => setFlashDirection(null), 300);
  };

  return (
    <div className="flex items-center">
      <input
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        className={`ace-input ${flashDirection === 'up' ? 'text-accent' : flashDirection === 'down' ? 'text-destructive' : 'text-foreground'} disabled:cursor-not-allowed disabled:opacity-50 transition-colors duration-200`}
      />
      <div className="flex flex-col ml-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => handleIncrement(step)}
          className={`rounded-sm p-1 text-muted-foreground transition-all hover:bg-primary/15 hover:text-accent ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        >
          <ChevronUp size={16} />
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => handleIncrement(-step)}
          className={`rounded-sm p-1 text-muted-foreground transition-all hover:bg-primary/15 hover:text-destructive ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        >
          <ChevronDown size={16} />
        </button>
      </div>
    </div>
  );
};

const validateResults = (results) => {
  console.log('Validating results:', JSON.stringify(results, null, 2));
  if (!results) {
    throw new Error('Results are undefined or null');
  }

  if (!results.annualSummary) {  // Changed from AnnualSummary to annualSummary
    console.error('Missing annualSummary. Results structure:', JSON.stringify(results, null, 2));
    throw new Error('annualSummary is missing from the results');
  }
  if (!Array.isArray(results.annualSummary)) {
    throw new Error('annualSummary is not an array');
  }
  if (results.annualSummary.length === 0) {
    throw new Error('annualSummary is an empty array');
  }
  if (!results.monthlySchedule) {
    throw new Error('monthlySchedule is missing from the results');
  }
  if (!Array.isArray(results.monthlySchedule)) {
    throw new Error('monthlySchedule is not an array');
  }
  if (results.monthlySchedule.length === 0) {
    throw new Error('monthlySchedule is an empty array');
  }
  if (!results.totalMetrics || typeof results.totalMetrics !== 'object') {
    throw new Error('Invalid totalMetrics');
  }
  // Add more specific checks here if needed
};

const RMProFormaDashboard = () => {
  // Initialize with one default RM entry
  const [entries, setEntries] = useState([
    {
      id: generateId(),
      type: 'RM',
      name: 'RM 1',
      formData: getDefaultFormData(),
      result: null,
      linkedRmId: null,
      offsetMonths: 0
    }
  ]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [consolidatedResult, setConsolidatedResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [yieldCurves, setYieldCurves] = useState(null);
  const [loadingPhase, setLoadingPhase] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const tabsContainerRef = useRef(null);
  const anchorRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [editingTabIndex, setEditingTabIndex] = useState(null);
  const [editingTabName, setEditingTabName] = useState('');

  // Get the currently active entry's formData for backward compatibility
  const formData = entries[activeIndex]?.formData || getDefaultFormData();
  const setFormData = (updater) => {
    setEntries(prev => {
      const newEntries = [...prev];
      if (typeof updater === 'function') {
        newEntries[activeIndex] = {
          ...newEntries[activeIndex],
          formData: updater(newEntries[activeIndex].formData)
        };
      } else {
        newEntries[activeIndex] = {
          ...newEntries[activeIndex],
          formData: updater
        };
      }
      return newEntries;
    });
  };

  // For backward compatibility with existing results display
  const results = entries[activeIndex]?.result;


  // Entry management functions
  const addEntry = (type, sourceEntry = null) => {
    let newEntry;
    const entryCount = entries.filter(e => e.type === type).length;

    if (type === 'Support') {
      newEntry = {
        id: generateId(),
        type: 'Support',
        name: `Support ${entryCount + 1}`,
        formData: getDefaultSupportData(),
        result: null,
        linkedRmId: entries.find(e => e.type === 'RM')?.id || null,
        offsetMonths: 0
      };
    } else if (sourceEntry) {
      // Duplicate existing RM
      newEntry = {
        id: generateId(),
        type: 'RM',
        name: `${sourceEntry.name} (Copy)`,
        formData: { ...sourceEntry.formData },
        result: null,
        linkedRmId: null,
        offsetMonths: 0
      };
    } else {
      // New standard RM
      newEntry = {
        id: generateId(),
        type: 'RM',
        name: `RM ${entryCount + 1}`,
        formData: getDefaultFormData(),
        result: null,
        linkedRmId: null,
        offsetMonths: 0
      };
    }

    setEntries(prev => {
      const newEntries = [...prev, newEntry];
      setActiveIndex(newEntries.length - 1); // Switch to the new tab
      return newEntries;
    });
  };

  const removeEntry = (index) => {
    if (entries.length <= 1) {
      setError("Cannot remove the last entry");
      return;
    }

    const entryToRemove = entries[index];

    // Check if any support staff are linked to this RM
    if (entryToRemove.type === 'RM') {
      const linkedSupports = entries.filter(e => e.linkedRmId === entryToRemove.id);
      if (linkedSupports.length > 0) {
        setError(`Cannot remove ${entryToRemove.name} - ${linkedSupports.length} support staff are linked to it`);
        return;
      }
    }

    setEntries(prev => prev.filter((_, i) => i !== index));
    if (activeIndex >= index && activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    }
  };

  const updateEntryLinking = (supportIndex, rmId, offsetMonths) => {
    setEntries(prev => {
      const newEntries = [...prev];
      const support = newEntries[supportIndex];
      if (support.type !== 'Support') return prev;

      support.linkedRmId = rmId;
      support.offsetMonths = offsetMonths;

      // Update support hire date based on linked RM
      if (rmId) {
        const linkedRm = newEntries.find(e => e.id === rmId);
        if (linkedRm) {
          const rmDate = new Date(linkedRm.formData.rmHireDate);
          rmDate.setMonth(rmDate.getMonth() + offsetMonths);
          support.formData.rmHireDate = rmDate.toISOString().split('T')[0];
          support.formData.firstProductionDate = support.formData.rmHireDate;
        }
      }

      return newEntries;
    });
  };

  const handleInputChange = useCallback((field, value, index = null) => {
    console.log('Handling input change:', field, value, index);
    setFormData(prevData => {
      // Guard: tolerate NaN/undefined without resetting to 0 prematurely
      if ((typeof value === 'number' && Number.isNaN(value)) || value === undefined) {
        return prevData;
      }
      if (field === 'rmType') {
        console.log('RM Type changed to:', value);
        // Get defaults and merge them directly
        const defaults = getDefaultsForRMType(value);
        return {
          ...prevData,
          ...defaults,
          rmType: value,
          rmHireDate: prevData.rmHireDate,
          firstProductionDate: prevData.firstProductionDate
        };
      }
      if (index !== null) {
        const newArray = [...prevData[field]];
        newArray[index] = { ...newArray[index], ...value };
        return { ...prevData, [field]: newArray };
      }

      // Clamp percentage fields for stability
      const clamp01 = (n) => Math.max(0, Math.min(1, n));
      const percentageFields = new Set([
        'annualMeritIncrease',
        'incentiveCompensationPercentage',
        'loanVsLinePercentage',
        'lineUtilizationPercentage',
        'originationFeePercentage',
        'unusedCommitmentFeePercentage',
        'originationFeePercentage',
        'unusedCommitmentFeePercentage',
        'prepayPercentageOfBalance',
        'provisionPercentage'
      ]);
      const nextValue = (typeof value === 'number' && percentageFields.has(field)) ? clamp01(value) : value;
      return { ...prevData, [field]: nextValue };
    });
  }, []);

  useEffect(() => {
    console.log('Form data updated:', formData);
  }, [formData]);

  useEffect(() => {
    const dateError = validateDateInputs(formData.rmHireDate, formData.firstProductionDate);
    setError(dateError);
  }, [formData.rmHireDate, formData.firstProductionDate]);

  const OPTIONAL_ZERO_FIELDS = new Set([
    'rmType',
    'rmHireDate',
    'firstProductionDate',
    'depositLagMonths',
    'depositRateFloor',
    'depositRateFloorEnabled',
    'lineShortTenorYears',
    'depositAnchorTenorYears',
    'linesPricingMode'
  ]);

  const sanitizeFormData = (formData, curvesOverride = null) => {
    const sanitizeNumber = (n) => (Number.isFinite(n) ? n : 0);
    const sanitizeArray = (arr) => (Array.isArray(arr) ? arr.map(row => Object.fromEntries(Object.entries(row).map(([k, v]) => [k, sanitizeNumber(v)]))) : []);
    const depositRateFloor = formData.depositRateFloorEnabled &&
      formData.depositRateFloor !== null &&
      formData.depositRateFloor !== undefined
      ? sanitizeNumber(formData.depositRateFloor)
      : null;

    return {
      ...formData,
      salary: sanitizeNumber(formData.salary),
      annualMeritIncrease: sanitizeNumber(formData.annualMeritIncrease),
      incentiveCompensationPercentage: sanitizeNumber(formData.incentiveCompensationPercentage),
      discretionaryExpenses: sanitizeNumber(formData.discretionaryExpenses),
      deferredCostsPerLoan: sanitizeNumber(formData.deferredCostsPerLoan),
      avgLoanExposureAtOrigination: sanitizeNumber(formData.avgLoanExposureAtOrigination),
      loanVsLinePercentage: sanitizeNumber(formData.loanVsLinePercentage),
      lineUtilizationPercentage: sanitizeNumber(formData.lineUtilizationPercentage),
      originationFeePercentage: sanitizeNumber(formData.originationFeePercentage),
      unusedCommitmentFeePercentage: sanitizeNumber(formData.unusedCommitmentFeePercentage),
      prepayPercentageOfBalance: sanitizeNumber(formData.prepayPercentageOfBalance),
      averageLifeLoans: sanitizeNumber(formData.averageLifeLoans),
      averageLifeLines: sanitizeNumber(formData.averageLifeLines),
      averageLifeIncludesPrepay: !!formData.averageLifeIncludesPrepay,
      linesPricingMode: formData.linesPricingMode,
      lineShortTenorYears: sanitizeNumber(formData.lineShortTenorYears),
      depositLagMonths: sanitizeNumber(formData.depositLagMonths),
      depositAnchorTenorYears: sanitizeNumber(formData.depositAnchorTenorYears),
      depositRateFloor,
      depositRateFloorEnabled: !!formData.depositRateFloorEnabled,
      provisionPercentage: sanitizeNumber(formData.provisionPercentage),
      annualProduction: sanitizeArray(formData.annualProduction),
      goingOnYields: sanitizeArray(formData.goingOnYields),
      ...(curvesOverride ? { yieldCurves: curvesOverride } : {})
    };
  };

  const validateEntry = (entry) => {
    const errors = [];
    const data = entry.formData;

    if (!data.rmHireDate) {
      errors.push(`${entry.name}: Hire Date is required`);
    }
    if (!data.firstProductionDate) {
      errors.push(`${entry.name}: First Production Date is required`);
    }

    // Different validation for RM vs Support
    if (entry.type === 'Support') {
      // Support staff validation - only check essential fields
      if (data.salary <= 0) {
        errors.push(`${entry.name}: Salary must be greater than 0`);
      }
      // Don't validate production fields for support
    } else {
      // RM validation - check all production fields
      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (key === 'annualProduction' || key === 'goingOnYields') {
            value.forEach((item, index) => {
              Object.entries(item).forEach(([subKey, subValue]) => {
                if (subValue === 0) {
                  errors.push(`${entry.name}: ${key}[${index}].${subKey} is required and must be non-zero`);
                }
              });
            });
          }
        } else if (!OPTIONAL_ZERO_FIELDS.has(key) && value === 0) {
          errors.push(`${entry.name}: ${key} is required and must be non-zero`);
        }
      });

      if (data.loanVsLinePercentage < 0 || data.loanVsLinePercentage > 1) {
        errors.push(`${entry.name}: Loan vs Line Percentage must be between 0 and 100`);
      }
      if (data.provisionPercentage < 0 || data.provisionPercentage > 1) {
        errors.push(`${entry.name}: Provision Percentage must be between 0 and 100`);
      }
    }

    return errors;
  };

  const validateFormData = () => {
    // For backward compatibility - validate current entry
    return validateEntry(entries[activeIndex]);
  };

  const handleCalculate = async () => {
    setLoadingPhase('calculating');
    setIsLoading(true);
    setError(null);

    // Defer clearing existing results until we have fresh calculations

      const allErrors = [];
      entries.forEach(entry => {
        const dateError = validateDateInputs(entry.formData.rmHireDate, entry.formData.firstProductionDate);
        if (dateError) {
          allErrors.push(`${entry.name}: ${dateError}`);
        }
        const sanitized = sanitizeFormData(entry.formData);
        const validationErrors = validateEntry({ ...entry, formData: sanitized });
        allErrors.push(...validationErrors);
      });

    if (allErrors.length > 0) {
      setError(`Validation errors:\n${allErrors.join('\n')}`);
      setIsLoading(false);
      return;
    }

    try {
      // Calculate all entries in parallel
      const calculationPromises = entries.map(async (entry, index) => {
        try {
          const formattedData = sanitizeFormData(
            entry.formData,
            ensureYieldCurveCoverage(yieldCurves)
          );

          console.log(`Calculating for ${entry.name}:`, JSON.stringify(formattedData, null, 2));
          const proFormaResults = await apiService.calculateRMProForma(formattedData);

          validateResults(proFormaResults);

          // Format dates to ISO 8601 string
          if (proFormaResults.annualSummary) {
            proFormaResults.annualSummary = proFormaResults.annualSummary.map(item => ({
              ...item,
              period: format(parseISO(item.index), 'yyyy-MM-dd')
            }));
          }

          if (proFormaResults.monthlySchedule) {
            proFormaResults.monthlySchedule = proFormaResults.monthlySchedule.map(item => ({
              ...item,
              period: format(parseISO(item.index), 'yyyy-MM-dd')
            }));
          }

          return { index, result: proFormaResults, success: true };
        } catch (error) {
          console.error(`Calculation failed for ${entry.name}:`, error);
          return { index, error: error.message, success: false };
        }
      });

      const results = await Promise.all(calculationPromises);

      // Check for failures
      const failures = results.filter(r => !r.success);
      if (failures.length > 0) {
        const errorMessages = failures.map(f =>
          `${entries[f.index].name}: ${f.error}`
        );
        setError(`Calculation failed:\n${errorMessages.join('\n')}`);
        setIsLoading(false);
        return;
      }

      // Update entries with results
      setEntries(prev => prev.map((entry, index) => ({
        ...entry,
        result: results.find(r => r.index === index)?.result || entry.result
      })));

      const computedResults = results.map(r => r.result);
      if (computedResults.length > 1) {
        const consolidated = consolidateResults(computedResults);
        setConsolidatedResult(consolidated);
        console.log('Consolidated result:', consolidated);
      } else if (computedResults.length === 1) {
        setConsolidatedResult(computedResults[0]);
      }

      console.log('All calculations completed successfully');
    } catch (err) {
      console.error('Error calculating pro forma:', err);
      console.error('Error details:', JSON.stringify(err, null, 2));
      let errorMessage = 'An error occurred while calculating the pro forma';
      if (err.response && err.response.data && err.response.data.error) {
        errorMessage += `: ${err.response.data.error}`;
      } else if (err.message) {
        errorMessage += `: ${err.message}`;
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
      setLoadingPhase(null);
    }
  };

  // Excel export handler
  const handleExportToExcel = useCallback(() => {
    if (activeIndex === -1) {
      // Consolidated view
      if (canExport(consolidatedResult)) {
        exportToExcel(consolidatedResult.monthlySchedule, 'Consolidated', true);
      }
    } else {
      // Individual entry
      const entry = entries[activeIndex];
      if (entry && canExport(entry.result)) {
        exportToExcel(entry.result.monthlySchedule, entry.name, false);
      }
    }
  }, [activeIndex, consolidatedResult, entries]);

  const renderTooltip = (content) => (
    <Tooltip title={content}>
      <InfoIcon size={16} className="ml-1 text-muted-foreground" />
    </Tooltip>
  );

  const renderYearlyInputTable = (field, label, tooltipContent, options) => (
    <div className="mb-4">
      <Label>{label} {renderTooltip(tooltipContent)}</Label>
      <table className="w-full">
        <thead>
          <tr>
            <th className="px-2 py-1 text-left"></th>
            {['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5'].map((year, index) => (
              <th key={index} className="px-2 py-1 text-left">{year}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="px-2 py-1 font-medium">Loans</td>
            {formData[field].map((value, index) => (
              <td key={index} className="px-2 py-1">
                <CustomInput
                  value={value.loans}
                  onChange={(newValue) => handleInputChange(field, { loans: newValue }, index)}
                  options={options}
                />
              </td>
            ))}
          </tr>
          {field === 'goingOnYields' && (
            <tr>
              <td className="px-2 py-1 font-medium">Lines</td>
              {formData[field].map((value, index) => (
                <td key={index} className="px-2 py-1">
                  <CustomInput
                    value={value.lines}
                    onChange={(newValue) => handleInputChange(field, { lines: newValue }, index)}
                    options={options}
                  />
                </td>
              ))}
            </tr>
          )}
          <tr>
            <td className="px-2 py-1 font-medium">Deposits</td>
            {formData[field].map((value, index) => (
              <td key={index} className="px-2 py-1">
                <CustomInput
                  value={value.deposits}
                  onChange={(newValue) => handleInputChange(field, { deposits: newValue }, index)}
                  options={options}
                />
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );

  const renderSingleInput = (field, label, tooltipContent, options) => (
    <div className="mb-4">
      <Label>
        {label} {renderTooltip(tooltipContent)}
      </Label>
      <CustomInput
        value={formData[field]}
        onChange={(newValue) => handleInputChange(field, newValue)}
        options={options}
      />
    </div>
  );

  const LoadingIndicator = ({ message = 'Loading...' }) => (
    <div className="flex items-center justify-center p-4">
      <Loader2 className="w-8 h-8 animate-spin mr-2" />
      <span>{message}</span>
    </div>
  );

  useEffect(() => {
    const fetchYieldCurves = async () => {
      try {
        setLoadingPhase('curves');
        setIsLoading(true);
        // Fetch live curves from backend for charting: use FRED instantaneous forward rates for historical consistency
        const params = { provider: 'fred_instantaneous' };
        const data = await apiService.fetchYieldCurves(params);
        console.log('Fetched yield curves:', data);
        if (data && Object.keys(data).length > 0) {
          setYieldCurves(ensureYieldCurveCoverage(data));
        } else {
          throw new Error('No yield curve data received');
        }
      } catch (error) {
        console.error('Error fetching yield curves:', error);
        // Keep the workspace usable when live market data is temporarily unavailable.
        setYieldCurves(ensureYieldCurveCoverage());
        console.warn('Using static fallback yield curves after fetch failure.');
      } finally {
        setIsLoading(false);
        setLoadingPhase(null);
      }
    };

    fetchYieldCurves();
  }, []);

  // Close dropdown when clicking outside (use click to avoid early close races)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isDropdownOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !(anchorRef.current && anchorRef.current.contains(event.target))
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Ensure the active tab is visible after changes
  useEffect(() => {
    if (!tabsContainerRef.current) return;
    try {
      const target = tabsContainerRef.current.querySelector(`[data-tab-index="${activeIndex}"]`);
      if (target && target.scrollIntoView) {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'end' });
      } else {
        tabsContainerRef.current.scrollLeft = tabsContainerRef.current.scrollWidth;
      }
    } catch (e) {
      // no-op
    }
  }, [activeIndex, entries.length]);

  return (
    <div>
      <section className="ace-rm-overview">
        <span className="ace-rm-eyebrow">OP_PRLX · rm-pro-forma</span>
        <h1 className="ace-rm-title">
          Model every RM hire with
          <span className="ace-serif text-primary"> hub-grade consistency.</span>
        </h1>
        <p className="ace-rm-description">
          Configure hire cohorts, support staffing, yield assumptions, and expense profiles in one place.
          The workspace keeps assumptions traceable and outputs aligned with Ace Analytics hub styling.
        </p>
      </section>
      <Card className="card-wrapper overflow-hidden">
      <CardHeader className="border-b border-border/60 bg-card/45">
        <h2 className="mb-1 text-2xl font-bold text-primary">RM Pro Forma Workspace</h2>
      </CardHeader>
      <CardContent className="pt-5">
        {/* Tab Navigation */}
        <div className="mb-4 border-b border-border/60 pb-2">
          <div className="flex items-center gap-2">
            <div className="flex space-x-1 overflow-x-auto flex-1 min-w-0" ref={tabsContainerRef}>
              {entries.map((entry, index) => (
                editingTabIndex === index ? (
                  <input
                    key={`${entry.id}-edit`}
                    type="text"
                    value={editingTabName}
                    autoFocus
                    onChange={(e) => setEditingTabName(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const newName = editingTabName.trim();
                        if (newName) {
                          setEntries(prev => {
                            const next = [...prev];
                            next[index] = { ...next[index], name: newName };
                            return next;
                          });
                        }
                        setEditingTabIndex(null);
                        setEditingTabName('');
                      } else if (e.key === 'Escape') {
                        setEditingTabIndex(null);
                        setEditingTabName('');
                      }
                    }}
                    onBlur={() => {
                      const newName = editingTabName.trim();
                      if (newName) {
                        setEntries(prev => {
                          const next = [...prev];
                          next[index] = { ...next[index], name: newName };
                          return next;
                        });
                      }
                      setEditingTabIndex(null);
                      setEditingTabName('');
                    }}
                    className={`ace-input rounded-full px-3 py-2 text-sm font-medium ${entry.result ? (entry.type === 'Support' ? 'ring-1 ring-secondary/45' : 'ring-1 ring-accent/45') : ''}`}
                    style={{ minWidth: '8rem' }}
                  />
                ) : (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    onDoubleClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setEditingTabIndex(index);
                      setEditingTabName(entry.name || '');
                    }}
                    data-tab-index={index}
                    className={`px-4 py-2 text-sm font-medium rounded-full border transition-colors ${activeIndex === index
                      ? (entry.type === 'Support'
                        ? 'border-secondary bg-secondary text-secondary-foreground'
                        : 'border-primary bg-primary text-primary-foreground')
                      : 'border-border bg-card/80 text-foreground hover:bg-primary/12'
                      } ${entry.result ? (entry.type === 'Support' ? 'ring-1 ring-secondary/45' : 'ring-1 ring-accent/45') : ''}`}
                  >
                    {entry.name}
                    {entry.type === 'Support' && ' 👤'}
                  </button>
                )
              ))}
            </div>

            {/* Add Entry Dropdown */}
            <div className="relative inline-block">
              <button
                type="button"
                className="ace-button-secondary rounded-full"
                ref={anchorRef}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  try {
                    const rect = anchorRef.current?.getBoundingClientRect();
                    if (rect) {
                      setMenuPos({ top: Math.round(rect.bottom), left: Math.round(rect.left) });
                    }
                  } catch { }
                  console.log('Add Entry clicked');
                  setIsDropdownOpen(prev => !prev);
                }}
                aria-haspopup="menu"
                aria-expanded={isDropdownOpen}
              >
                {`+ Add Entry ${isDropdownOpen ? '▲' : '▼'}`}
              </button>
              {process.env.NODE_ENV !== 'production' && (
                <button
                  type="button"
                  className="ml-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-primary/12 hover:text-foreground"
                  onClick={() => {
                    console.log('Quick Add RM');
                    addEntry('RM');
                  }}
                >
                  + Quick Add RM
                </button>
              )}
              {isDropdownOpen && createPortal(
                (
                  <div
                    ref={dropdownRef}
                    className="ace-floating-surface fixed z-[9999] w-56 p-1"
                    style={{ top: `${menuPos.top}px`, left: `${menuPos.left}px` }}
                    role="menu"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        console.log('AddEntry: New Standard RM');
                        addEntry('RM');
                        setIsDropdownOpen(false);
                      }}
                      className="ace-floating-item block w-full px-3 py-2 text-left text-sm"
                      role="menuitem"
                    >
                      New Standard RM
                    </button>
                    {entries[activeIndex]?.type === 'RM' && (
                      <button
                        type="button"
                        onClick={() => {
                          console.log('AddEntry: Duplicate Current RM');
                          addEntry('RM', entries[activeIndex]);
                          setIsDropdownOpen(false);
                        }}
                        className="ace-floating-item block w-full px-3 py-2 text-left text-sm"
                        role="menuitem"
                      >
                        Duplicate Current RM
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        console.log('AddEntry: New Support Staff');
                        addEntry('Support');
                        setIsDropdownOpen(false);
                      }}
                      className="ace-floating-item block w-full px-3 py-2 text-left text-sm"
                      role="menuitem"
                    >
                      New Support Staff
                    </button>
                  </div>
                ),
                document.body
              )}
            </div>

            {/* Consolidated Tab (if multiple entries exist or results available) */}
            {(entries.length > 1 || consolidatedResult) && (
              <button
                type="button"
                onClick={() => setActiveIndex(-1)}
                className={`ml-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${activeIndex === -1
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-card/80 text-foreground hover:bg-primary/12'
                  }`}
              >
                📊 Consolidated View
              </button>
            )}
          </div>
        </div>

        {/* Remove Entry Button */}
        {entries.length > 1 && activeIndex >= 0 && (
          <button
            type="button"
            onClick={() => removeEntry(activeIndex)}
            className="mb-2 rounded-full border border-destructive/45 px-3 py-1 text-xs text-destructive transition-colors hover:bg-destructive/15"
          >
            Remove {entries[activeIndex]?.name}
          </button>
        )}

        <form onSubmit={(e) => { e.preventDefault(); handleCalculate(); }}>
          {error && (
            <div className="mb-4 rounded-xl border border-destructive/45 bg-destructive/15 p-4 text-destructive">
              {error}
            </div>
          )}
          {/* Conditional rendering based on entry type */}
          {entries[activeIndex]?.type === 'Support' ? (
            // Support Staff Form
            <div className="space-y-4">
              <div className="rounded-xl border border-border bg-card/75 p-4">
                <h3 className="mb-2 font-semibold text-foreground">Support Staff Configuration</h3>

                {/* Linking to RM */}
                <div className="mb-4">
                  <Label>
                    Linked to RM {renderTooltip("Select which RM this support staff is assigned to.")}
                  </Label>
                  <Select
                    value={entries[activeIndex]?.linkedRmId || ''}
                    onValueChange={(value) => {
                      const offsetMonths = entries[activeIndex]?.offsetMonths || 0;
                      updateEntryLinking(activeIndex, value, offsetMonths);
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select RM" />
                    </SelectTrigger>
                    <SelectContent>
                      {entries.filter(e => e.type === 'RM').map(rm => (
                        <SelectItem key={rm.id} value={rm.id}>{rm.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Offset Months */}
                <div className="mb-4">
                  <Label>
                    Start Offset (months from RM) {renderTooltip("Number of months after (positive) or before (negative) the RM hire date.")}
                  </Label>
                  <input
                    type="number"
                    value={entries[activeIndex]?.offsetMonths || 0}
                    onChange={(e) => {
                      const offset = parseInt(e.target.value) || 0;
                      updateEntryLinking(activeIndex, entries[activeIndex]?.linkedRmId, offset);
                    }}
                    className="ace-input"
                  />
                </div>

                {/* Support Hire Date (calculated) */}
                <div className="mb-4">
                  <Label>
                    Support Hire Date {renderTooltip("Calculated based on linked RM and offset.")}
                  </Label>
                  <input
                    type="date"
                    value={formData.rmHireDate}
                    onChange={(e) => handleInputChange('rmHireDate', e.target.value)}
                    className="ace-input"
                  />
                </div>
              </div>

              {/* Support Expenses */}
              <div className="rounded-xl border border-border bg-card/75 p-4">
                <h3 className="mb-4 font-semibold text-foreground">Support Staff Expenses</h3>
                {renderSingleInput('salary', 'Salary', 'Enter the annual salary for the support staff.', { useCommas: true, decimals: 0, step: 1000, commitOnChange: false })}
                {renderSingleInput('annualMeritIncrease', 'Annual Merit Increase', 'Enter the annual merit increase percentage.', { isPercentage: true, decimals: 1, step: 0.001, commitOnChange: false })}
                {renderSingleInput('discretionaryExpenses', 'Discretionary Expenses', 'Enter the annual discretionary expenses.', { useCommas: true, decimals: 0, step: 100, commitOnChange: false })}
                {renderSingleInput('incentiveCompensationPercentage', 'Incentive Compensation Percentage', 'Enter the incentive compensation percentage (usually 0 for support).', { isPercentage: true, decimals: 1, step: 0.005, commitOnChange: true })}
              </div>
            </div>
          ) : (
            // RM Form (existing)
            <Accordion type="single" collapsible defaultValue="rm-type-start">
              <AccordionItem value="rm-type-start">
                <AccordionTrigger>RM Type & Start</AccordionTrigger>
                <AccordionContent>
                  <div className="mb-4">
                    <Label>
                      RM Type {renderTooltip("Select the type of Relationship Manager.")}
                    </Label>
                    <Select
                      value={formData.rmType}
                      onValueChange={(value) => {
                        console.log('Select onValueChange called with:', value);
                        handleInputChange('rmType', value);
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select RM Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="commercial_rm">Commercial RM</SelectItem>
                        <SelectItem value="commercial_rm_senior">Commercial RM, Senior</SelectItem>
                        <SelectItem value="private_banker">Private Banker</SelectItem>
                        <SelectItem value="private_banker_senior">Private Banker, Senior</SelectItem>
                        <SelectItem value="small_business_banker">Small Business Banker</SelectItem>
                        <SelectItem value="small_business_banker_senior">Small Business Banker, Senior</SelectItem>
                        <SelectItem value="mortgage_loan_officer">Mortgage Loan Officer</SelectItem>
                        <SelectItem value="mortgage_loan_officer_senior">Mortgage Loan Officer, Senior</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="mb-4">
                    <Label>
                      RM Hire Date {renderTooltip("Enter the date when the Relationship Manager was hired.")}
                    </Label>
                    <input
                      type="date"
                      value={formData.rmHireDate}
                      onChange={(e) => handleInputChange('rmHireDate', e.target.value)}
                      className="ace-input"
                    />
                  </div>
                  <div className="mb-4">
                    <Label>
                      First Production Date {renderTooltip("Enter the date when the Relationship Manager started production. This must be on or after the RM Hire Date.")}
                    </Label>
                    <input
                      type="date"
                      value={formData.firstProductionDate}
                      onChange={(e) => handleInputChange('firstProductionDate', e.target.value)}
                      className="ace-input"
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-1">
                <AccordionTrigger>Balance Sheet: Volume, Pricing, & Dynamics</AccordionTrigger>
                <AccordionContent>
                  {renderYearlyInputTable('annualProduction', 'Annual Funded Loan and Deposit Production', 'Enter the expected annual production for loans and deposits over the next 5 years.', { useCommas: true, decimals: 0, step: 10000 })}
                  {renderSingleInput('loanVsLinePercentage', 'Loan vs Line Production Percentage', 'Enter the average percentage of loan production vs line production.', { isPercentage: true, decimals: 1, step: 0.01 })}
                  {renderYearlyInputTable(
                    'goingOnYields',
                    'Going-on Spreads: Loans/Lines (Credit Spreads) and Deposits (Rate Spread)',
                    'Enter the expected spreads (not all-in yields) applied to the base index from the yield curve for each year. Loans/Lines are credit spreads to the index; deposits are rate spreads. Example: 0.025 = 2.5%.',
                    { isPercentage: true, decimals: 2, step: 0.0001 }
                  )}
                  {renderSingleInput('lineUtilizationPercentage', 'Line Utilization Percentage', 'Enter the average percentage of line utilization.', { isPercentage: true, decimals: 1, step: 0.005 })}
                  {renderSingleInput('averageLifeLoans', 'Average Life of Loans', 'Enter the average life of loans in years.', { decimals: 2, step: 0.25 })}
                  {renderSingleInput('averageLifeLines', 'Average Life of Lines', 'Enter the average life of lines in years.', { decimals: 2, step: 0.25 })}
                  <div className="mb-4">
                    <Label>
                      Lines Pricing Mode {renderTooltip('Select how funded lines are priced: short-tenor float or average-life anchor.')}
                    </Label>
                    <Select
                      value={formData.linesPricingMode || 'short_float'}
                      onValueChange={(value) => handleInputChange('linesPricingMode', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select pricing mode" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="short_float">Short-Tenor Float</SelectItem>
                        <SelectItem value="avg_life">Average-Life Anchor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.linesPricingMode !== 'avg_life' && renderSingleInput(
                    'lineShortTenorYears',
                    'Line Short Tenor (Years)',
                    'Enter the short-tenor anchor for floating lines (e.g., 0.25 = 3 months).',
                    { decimals: 2, step: 0.05 }
                  )}
                  {/* Toggle: Average Life includes Prepayment (WAL) */}
                  <div className="mb-2 flex items-center justify-between">
                    <div>
                      <Label>
                        Average Life Includes Prepayment (WAL)
                        {renderTooltip('Treat Average Life of Loans as a WAL that already includes prepayments. When enabled, the explicit loan prepayment percentage is ignored to avoid double counting.')}
                      </Label>
                    </div>
                    <div className="ml-4">
                      <label className="inline-flex items-center cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={!!formData.averageLifeIncludesPrepay}
                          onChange={(e) => handleInputChange('averageLifeIncludesPrepay', e.target.checked)}
                          className="form-checkbox h-5 w-5 text-primary"
                        />
                        <span className="ml-2 text-sm text-foreground">Enable</span>
                      </label>
                    </div>
                  </div>
                  {renderSingleInput('prepayPercentageOfBalance', 'Prepay Percentage of Balance', 'Enter the expected prepayment percentage of the balance. Disabled when WAL toggle is on.', { isPercentage: true, decimals: 2, step: 0.005, disabled: !!formData.averageLifeIncludesPrepay })}
                  {renderSingleInput('depositLagMonths', 'Deposit Rate Lag (Months)', 'Enter the number of months over which deposit rates catch up to the target rate.', { decimals: 0, step: 1 })}
                  {renderSingleInput('depositAnchorTenorYears', 'Deposit Anchor Tenor (Years)', 'Enter the base curve tenor used for deposit pricing (e.g., 0.5 = 6 months).', { decimals: 2, step: 0.25 })}
                  <div className="mb-2 flex items-center justify-between">
                    <div>
                      <Label>
                        Enable Deposit Rate Floor
                        {renderTooltip('When enabled, deposit rates are floored at the provided annualized rate. Disable to allow negative rates.')}
                      </Label>
                    </div>
                    <div className="ml-4">
                      <label className="inline-flex items-center cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={!!formData.depositRateFloorEnabled}
                          onChange={(e) => {
                            const enabled = e.target.checked;
                            handleInputChange('depositRateFloorEnabled', enabled);
                            if (enabled && formData.depositRateFloor == null) {
                              handleInputChange('depositRateFloor', 0.0);
                            } else if (!enabled) {
                              handleInputChange('depositRateFloor', null);
                            }
                          }}
                          className="form-checkbox h-5 w-5 text-primary"
                        />
                        <span className="ml-2 text-sm text-foreground">Enable</span>
                      </label>
                    </div>
                  </div>
                  <div className="mb-4">
                    <Label>
                      Deposit Rate Floor
                      {renderTooltip('Enter the annualized deposit rate floor (e.g., 0.00% or -0.50%).')}
                    </Label>
                    <CustomInput
                      value={formData.depositRateFloor ?? ''}
                      onChange={(newValue) => handleInputChange('depositRateFloor', newValue)}
                      options={{ isPercentage: true, decimals: 2, step: 0.0005, disabled: !formData.depositRateFloorEnabled }}
                    />
                    {!formData.depositRateFloorEnabled && (
                      <p className="mt-1 text-xs text-muted-foreground">(Not applied)</p>
                    )}
                  </div>
                  {renderSingleInput('provisionPercentage', 'Provision Percentage', 'Enter the provision expense as a percentage of new loan production.', { isPercentage: true, decimals: 2, step: 0.001 })}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2">
                <AccordionTrigger>Fee Revenue</AccordionTrigger>
                <AccordionContent>
                  {renderSingleInput('originationFeePercentage', 'Origination Fee Percentage', 'Enter the percentage for origination fees.', { isPercentage: true, decimals: 2, step: 0.0001 })}
                  {renderSingleInput('unusedCommitmentFeePercentage', 'Unused Commitment Fee Percentage', 'Enter the percentage for unused commitment fees.', { isPercentage: true, decimals: 2, step: 0.0001 })}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3">
                <AccordionTrigger>Direct Expenses</AccordionTrigger>
                <AccordionContent>
                  {renderSingleInput('salary', 'Salary', 'Enter the annual salary for the salesperson.', { useCommas: true, decimals: 0, step: 1000, commitOnChange: false })}
                  {renderSingleInput('annualMeritIncrease', 'Annual Merit Increase', 'Enter the percentage for annual merit increases.', { isPercentage: true, decimals: 1, step: 0.005, commitOnChange: false })}
                  {renderSingleInput('incentiveCompensationPercentage', 'Incentive Compensation %', 'Annual Incentive Compensation % as a percentage of base pay.', { isPercentage: true, decimals: 1, step: 0.005, commitOnChange: true })}
                  {renderSingleInput('discretionaryExpenses', 'Discretionary Expenses', 'Enter the amount for annual discretionary expenses.', { useCommas: true, decimals: 0, step: 1000 })}
                  {renderSingleInput('deferredCostsPerLoan', 'Deferred Costs per Loan', 'Enter the amount of deferred costs per loan.', { useCommas: true, decimals: 0, step: 50 })}
                  {renderSingleInput('avgLoanExposureAtOrigination', 'Avg Loan Exposure at Origination', 'Enter the average loan exposure amount at origination.', { useCommas: true, decimals: 0, step: 50000 })}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}

          <Button type="submit" className="mt-4" disabled={isLoading}>
            {isLoading ? 'Calculating...' : 'Calculate Pro Forma'}
          </Button>
        </form>

        {isLoading ? (
          <LoadingIndicator message={loadingPhase === 'curves' ? 'Pulling yield curves...' : 'Calculating Pro Forma...'} />
        ) : error ? (
          <div className="text-destructive">{error}</div>
        ) : activeIndex === -1 ? (
          // Consolidated View
          <>
            {consolidatedResult ? (
              <div className="space-y-6">
                <div className="flex items-start justify-between rounded-xl border border-primary/35 bg-primary/12 p-4">
                  <div>
                    <h3 className="mb-2 font-semibold text-primary">
                      📊 Consolidated Pro Forma Results
                    </h3>
                    <p className="text-sm text-foreground">
                      Combined analysis of {entries.length} entries ({entries.filter(e => e.type === 'RM').length} RMs, {entries.filter(e => e.type === 'Support').length} Support)
                    </p>
                    {consolidatedResult.isConsolidated && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        Support staff costs: {entries.filter(e => e.type === 'Support').map(e => e.name).join(', ')} are included in expenses
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    onClick={handleExportToExcel}
                    className="flex items-center"
                  >
                    <FileDown className="w-4 h-4 mr-2" />
                    Export to Excel
                  </Button>
                </div>

                {yieldCurves && Object.keys(yieldCurves).length > 0 && (
                  <RMProFormaYieldCurve yieldCurves={yieldCurves} />
                )}

                <ErrorBoundary>
                  <RMCumulativePayback
                    annualSummary={consolidatedResult.annualSummary}
                    monthlySchedule={consolidatedResult.monthlySchedule}
                    cumulativePayback={consolidatedResult.cumulativePayback}
                    paybackDate={consolidatedResult.paybackDate}
                    isLoading={isLoading}
                    error={error}
                  />
                  <RMProFormaStatements
                    annualSummary={consolidatedResult.annualSummary}
                    monthlySchedule={consolidatedResult.monthlySchedule}
                    isLoading={isLoading}
                    error={error}
                  />
                  <MonthlyAnnualIncomeStatement
                    annualSummary={consolidatedResult.annualSummary}
                    monthlySchedule={consolidatedResult.monthlySchedule}
                    totalMetrics={consolidatedResult.totalMetrics}
                  />
                  <MonthlyAnnualBalanceSheet
                    annualSummary={consolidatedResult.annualSummary}
                    monthlySchedule={consolidatedResult.monthlySchedule}
                    totalMetrics={consolidatedResult.totalMetrics}
                  />
                </ErrorBoundary>
              </div>
            ) : (
              <div className="p-4 text-center text-muted-foreground">
                <p>No consolidated results available.</p>
                <p className="text-sm mt-1">Please calculate the Pro Forma for all entries first.</p>
              </div>
            )}
          </>
        ) : (
          // Individual Entry View
          <>
            {yieldCurves && Object.keys(yieldCurves).length > 0 ? (
              <RMProFormaYieldCurve yieldCurves={yieldCurves} />
            ) : (
              <div>No yield curve data available</div>
            )}
            {results && results.monthlySchedule && (
              <div className="flex justify-end mb-4">
                <Button
                  type="button"
                  onClick={handleExportToExcel}
                  className="flex items-center"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Export to Excel
                </Button>
              </div>
            )}
            {results && results.annualSummary && results.monthlySchedule && results.totalMetrics ? (
              <ErrorBoundary>
                <RMCumulativePayback
                  annualSummary={results.annualSummary}
                  monthlySchedule={results.monthlySchedule}
                  cumulativePayback={results.cumulativePayback}
                  paybackDate={results.paybackDate}
                  isLoading={isLoading}
                  error={error}
                />
                <RMProFormaStatements
                  annualSummary={results.annualSummary}
                  monthlySchedule={results.monthlySchedule}
                  isLoading={isLoading}
                  error={error}
                />
                <MonthlyAnnualIncomeStatement
                  annualSummary={results.annualSummary}
                  monthlySchedule={results.monthlySchedule}
                  totalMetrics={results.totalMetrics}
                />
                <MonthlyAnnualBalanceSheet
                  annualSummary={results.annualSummary}
                  monthlySchedule={results.monthlySchedule}
                  totalMetrics={results.totalMetrics}
                />
              </ErrorBoundary>
            ) : (
              <div>No results available. Please calculate the Pro Forma first.</div>
            )}
          </>
        )}
      </CardContent>
      </Card>
    </div>
  );
};

export default RMProFormaDashboard;
